// TopicsTab — the Data structure page's Topics tab: debounced search over the
// paged topic-sets list (POST /topic-sets/all), the topic table with a
// selection bar and per-row kebab, plus CreateTopicModal serving create
// (opened from the page header's Add button — the open state lives with the
// page and arrives as a prop) and edit (opened from the kebab or the bar).
//
// Verb gates: Edit rides `edit`; Delete rides `delete`. Both Delete surfaces
// (kebab item, bar button) feed one flow: a danger ConfirmModal over POST
// /topic-sets/bulk-delete, whose id-array body makes a single row and a whole
// selection the same call (the OptionSetsTab idiom throughout). The toolbar
// carries a one-category FilterDropdown (Last updated via the shared date
// presets) and the headers sort server-side over the confirmed TopicSortKey
// pair (`orderBy: { key, type }`, default Last updated desc).

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ConfirmModal,
  DataTable,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  TableFooter,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import {
  TopicSortKey,
  useBulkDeleteTopicsMutation,
  useGetTopicsQuery,
  type Topic,
} from '@/api/endpoints/topicSetsApi';
import { FormDateFilter } from '@/api/endpoints/formsApi';
import {
  DATE_ANY,
  buildDateFilterOptions,
  dateFilterOf,
  dateRangeOf,
} from '@/utils/dateFilterPresets';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useStickyTotal from '@/hooks/useStickyTotal';
import useModuleAccess from '@/hooks/useModuleAccess';
import CreateTopicModal from './CreateTopicModal';
import { useTopicColumns } from './topicColumns';

interface TopicsTabProps {
  /** The page header owns the Add button, so the modal's open state is lifted. */
  createOpen: boolean;
  onCloseCreate: () => void;
  /** Reports the UNFILTERED list total for the page's tab badge (lastTotals). */
  onCount?: (n: number) => void;
}

// Toolbar Filter keys + the empty selection shape (one category here).
const FILTER_KEYS = ['lastUpdated'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

type SortType = 'asc' | 'desc';

const TopicsTab = ({ createOpen, onCloseCreate, onCount }: TopicsTabProps) => {
  const { t } = useTranslation();
  const { canEdit, canDelete } = useModuleAccess('data_structure');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  // Custom-range FROM/TO for the Last updated category.
  const [dateRanges, setDateRanges] = useState<Record<string, FilterDropdownDateRange>>({});
  // Default matches the design: Last updated, newest first.
  const [sort, setSort] = useState<{ key: TopicSortKey; type: SortType }>({
    key: TopicSortKey.UpdatedAt,
    type: 'desc',
  });
  // Keyed by id but holding the whole row: the bar's actions need the selected
  // topic's values, and a row picked on page 1 is no longer in `items` once you
  // page away (the InspectionPage idiom).
  const [selected, setSelected] = useState<Map<string, Topic>>(new Map());
  const [editTopic, setEditTopic] = useState<Topic | null>(null);
  // The rows awaiting delete confirmation — one from the kebab, any number
  // from the selection bar; null = no dialog.
  const [deleting, setDeleting] = useState<Topic[] | null>(null);
  const [bulkDeleteTopics, { isLoading: deletingBusy }] = useBulkDeleteTopicsMutation();

  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'lastUpdated',
        label: t('data_structure_tp_col_updated'),
        icon: 'history',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: buildDateFilterOptions(t),
      },
    ],
    [t],
  );

  const lastUpdated = dateFilterOf(filters, 'lastUpdated');

  const { data, currentData, isLoading, isFetching, isError } = useGetTopicsQuery(
    {
      page,
      limit,
      search: debouncedSearch || undefined,
      lastUpdated,
      lastUpdatedStart: dateRangeOf(dateRanges, 'lastUpdated', lastUpdated, 'start'),
      lastUpdatedEnd: dateRangeOf(dateRanges, 'lastUpdated', lastUpdated, 'end'),
      orderBy: sort,
    },
    { refetchOnMountOrArgChange: true },
  );

  // A new search, filter or sort narrows or reorders the set — snap back to
  // page 1 so the pager can't sit past the end of the new list.
  useEffect(() => setPage(1), [debouncedSearch, filters, dateRanges, sort]);

  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);

  // Report the badge total only while search + filters are empty, so it stays
  // the true entity count rather than the current narrowing's.
  const filtersActive =
    !!debouncedSearch.trim() || FILTER_KEYS.some((key) => filters[key].size > 0);
  useEffect(() => {
    if (data && !filtersActive) onCount?.(total);
  }, [data, total, filtersActive, onCount]);

  const items = data?.items ?? [];
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  const clearSelection = () => setSelected(new Map());

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((tp) => tp.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });
  const allSelected = items.length > 0 && items.every((tp) => selected.has(tp.id));
  const someSelected = !allSelected && items.some((tp) => selected.has(tp.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (items.every((tp) => prev.has(tp.id))) items.forEach((tp) => next.delete(tp.id));
      else items.forEach((tp) => next.set(tp.id, tp));
      return next;
    });

  // The bar's Edit applies to exactly one row.
  const onlySelected = selected.size === 1 ? [...selected.values()][0] : null;

  // One request either way, so there is no partial outcome to reconcile: on
  // failure the selection is left intact and a retry needs no re-ticking.
  const runDelete = async () => {
    if (!deleting) return;
    try {
      await bulkDeleteTopics(deleting.map((tp) => tp.id)).unwrap();
      toast.success(t('data_structure_tp_deleted', { count: deleting.length }));
      clearSelection();
      setDeleting(null);
    } catch {
      toast.error(t('error_generic'));
      setDeleting(null);
    }
  };

  const columns = useTopicColumns({
    onEdit: canEdit ? setEditTopic : undefined,
    onDelete: canDelete ? (tp) => setDeleting([tp]) : undefined,
  });

  // Same key toggles direction; a new key resets to desc. DataTable types
  // onSort as (key: string), but only sortable columns can fire it and each is
  // keyed by a TopicSortKey — hence the cast at the boundary.
  const handleSort = (raw: string) => {
    const key = raw as TopicSortKey;
    setSort((prev) =>
      prev.key === key
        ? { key, type: prev.type === 'asc' ? 'desc' : 'asc' }
        : { key, type: 'desc' },
    );
  };

  return (
    <>
      <div className='data-structure-page__toolbar'>
        <div className='data-structure-page__toolbar-lead'>
          <div className='data-structure-page__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('data_structure_tp_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='data-structure-page__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('data_structure_tp_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
          <FilterDropdown
            label={t('action_filter')}
            categories={filterCategories}
            value={filters}
            ranges={dateRanges}
            onApply={(next, ranges) => {
              setFilters(next);
              setDateRanges(ranges);
            }}
            emptyText={t('text_no_results_found')}
          />
        </div>
        {items.length > 0 && (
          <span className='data-structure-page__meta'>
            {t('data_structure_tp_meta', { count: total })}
          </span>
        )}
      </div>

      <Card padded={false} className='data-structure-page__panel'>
        {selected.size > 0 && (
          <div className='data-structure-page__selection-bar'>
            <div className='data-structure-page__selection-lead'>
              <span className='data-structure-page__selected'>
                {t('data_structure_tp_selected', { count: selected.size })}
              </span>
              {/* Edit acts on ONE topic, so it only appears for a single
                  selection; Delete takes any size (one bulk call). Divider only
                  when something can follow it. */}
              {((onlySelected && canEdit) || canDelete) && (
                <>
                  <span className='data-structure-page__selection-divider' />
                  {onlySelected && canEdit && (
                    <Button
                      variant='ghost'
                      iconStart='pencil'
                      onClick={() => setEditTopic(onlySelected)}
                    >
                      {t('data_structure_tp_action_edit')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant='ghost'
                      iconStart='trash-2'
                      onClick={() => setDeleting([...selected.values()])}
                    >
                      {t('data_structure_tp_action_delete')}
                    </Button>
                  )}
                </>
              )}
            </div>
            <Button variant='ghost' onClick={clearSelection}>
              {t('button_clear')}
            </Button>
          </div>
        )}
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(tp) => tp.id}
          // Selection only feeds the bar's Edit/Delete — without both verbs it
          // would select into a bar of count + Clear.
          selectable={canEdit || canDelete}
          selectedIds={selectedIdSet}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectAllLabel={t('data_structure_tp_select_all')}
          sort={sort}
          onSort={handleSort}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={900}
          empty={
            <>
              <Icon name='bookmark' size={26} />
              {t('data_structure_tp_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[10, 20, 50]}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('data_structure_items_per_page')}
          rangeText={t('data_structure_tp_range', { from, to, total })}
        />
      </Card>

      {/* One instance for both modes — `topic` present means edit. */}
      <CreateTopicModal
        open={createOpen || Boolean(editTopic)}
        topic={editTopic}
        onClose={() => {
          onCloseCreate();
          setEditTopic(null);
        }}
      />

      {deleting && (
        <ConfirmModal
          open
          onClose={() => setDeleting(null)}
          onConfirm={runDelete}
          tone='danger'
          icon='trash-2'
          title={t('data_structure_tp_delete_title', { count: deleting.length })}
          description={t('data_structure_tp_delete_body', { count: deleting.length })}
          confirmLabel={t('data_structure_tp_action_delete')}
          confirmVariant='danger'
          confirmIcon='trash-2'
          loading={deletingBusy}
        />
      )}
    </>
  );
};

export default TopicsTab;
